import { Injectable } from '@angular/core';
import { CanDeactivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { ROUTER_CANCEL, ROUTER_NAVIGATED } from '@ngrx/router-store';
import { createAction, Store } from '@ngrx/store';
import { concat, merge, Observable, of, Subscription } from 'rxjs';
import { every, first, map, mapTo, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { ValuesType } from 'utility-types';
import { OTPSMSRes } from '../../form-core/api/api.service';
import { CheckerService } from '../../form-core/checker/checker.service';
import { DataService } from '../../form-core/data/data.service';
import { ErrorHandlerService } from '../../form-core/error-handler/error-handler.service';
import { OtpService } from '../../form-core/otp/otp.service';
import { ServiceHandlerModel, ServiceID, ServiceModel, ServiceStepModel, StepModel } from '../../form-json.types';
import { StepComponent } from '../../step/step.component';

export const deactivateFail = createAction('[Deactivate Guard] current step deactivate fail')
export const loadingStart = createAction('[Deactivate Guard] current step service loading start')
export const loadingEnd = createAction('[Deactivate Guard] current step service loading end')

@Injectable({
  providedIn: 'root'
})
export class DeactivateGuard implements CanDeactivate<StepComponent> {
  private fromActivateFail: boolean = false
  private subscription: Subscription | undefined

  canDeactivate(
    component: StepComponent,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    let next = nextState?.root
    while (next?.firstChild) {
      next = next.firstChild
    }
    const nextStep = next?.paramMap.get('id')
    const steps = this.data.getSteps()
    const currentIndex = steps.findIndex(value => value.id === currentRoute.paramMap.get('id'))
    const currentStep = this.data.getModel<StepModel | ServiceStepModel>(steps[currentIndex])
    const nextIndex = steps.findIndex(value => value.id === nextStep)
    const stepFormGroup = this.data.stepsFormGroup[currentStep.id]
    if (nextIndex < currentIndex) return true
    if (stepFormGroup.invalid) {
      this.store.dispatch(deactivateFail())
      return false
    }
    if (currentStep.type === 'service-step' && !this.fromActivateFail) {
      const services = currentStep.relationships.services.data.map(service => this.data.getModel<ServiceModel>(service))
      const errorHandlers = currentStep.relationships['error-handlers'].data.map(errorHandler => this.data.getModel<ServiceHandlerModel>(errorHandler))
      const observables = services.map(service => {
        switch (service.type) {
          case 'otp-service':
            return service.attributes.async ? this.asyncHandling(this.otpService.initiate(service)) : this.syncHandling(this.otpService.initiate(service), 'otp-service')
        }
      })
      const finish$: Observable<any> = merge(
        this.actions$.pipe(ofType(ROUTER_NAVIGATED), mapTo(false)),
        this.actions$.pipe(ofType(ROUTER_CANCEL), mapTo(true))
      ).pipe(first(), shareReplay({ bufferSize: 1, refCount: true }))
      merge(...errorHandlers.map(handler => this.errorHandler.initiate(handler))).pipe(
        first(),
        takeUntil(finish$)
      ).subscribe()
      const preparedServices = currentStep.attributes['sequential-mode'] ? concat(...observables) : merge(...observables)
      this.store.dispatch(loadingStart())
      return preparedServices.pipe(
        every(res => res),
        tap(res => !res && this.store.dispatch(loadingEnd()), () => this.store.dispatch(loadingEnd())),
        tap(res => res && finish$.subscribe(flag => this.fromActivateFail = flag)),
      )
    }
    if (this.fromActivateFail && !this.subscription?.closed) this.subscription = this.actions$.pipe(ofType(ROUTER_NAVIGATED), first()).subscribe(() => this.fromActivateFail = false)
    return true
  }

  constructor(private data: DataService, private store: Store, private otpService: OtpService, private actions$: Actions, private errorHandler: ErrorHandlerService, private checker: CheckerService) { }

  private syncHandling<T extends OTPSMSRes>(observable: Observable<T>, type: ValuesType<Omit<ServiceID, 'id'>>): Observable<boolean> {
    return observable.pipe(map(res => this.checker.assign(type)(res)))
  }

  private asyncHandling(observable: Observable<any>) {
    return of(true).pipe(tap(() => observable.subscribe()))
  }

}